import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ContainerFluid } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import useQueryParams from '../../hooks/useQueryParams';
import alertService from '../../services/AlertService';
import userService from '../../services/userService';
import uuid from '../../services/uuid';
import windowService from '../../services/windowService';
import { handleError } from '../../utils/apiUtils';
import { useLogin, useUser } from '../authentication';
import { createOrgAccount } from '../org';
import { getTrainingOverview, selfAssignTraining } from '../training';
import { getMemberByEmail, OrgMembership } from './api/getMemberByEmail';
import Wizard from './Wizard';

const LIFEWAY_ORG_IDS = [
  '7eff9784-c290-44b4-9364-9961f3ca3859', // Lifeway
  'd0c6eb46-1784-49ae-960c-fc82e3e94dd2', // Lifeway Employees
  'fb782e99-b1d7-44df-bc18-206922301c8b', // Lifeway Kids
];

type RouteParams = {
  onboardingName?: string;
  action?: string;
};

type Training = {
  trainingId: string;
  trainingType: string;
};

type UserLookupResponse = { id?: string | null } | null;

const OnboardingContainer = styled(ContainerFluid)`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const selectTraining = (name?: string): Training | null => {
  if (name === 'churchcares') {
    return {
      trainingId: 'ac19f483-b98a-4d91-a4bf-47e7bfb57e69',
      trainingType: 'Tasklist',
    };
  }
  return null;
};

export const ChurchCares = () => {
  const { onboardingName } = useParams<RouteParams>();
  const { user } = useUser();
  const { redirectToLogin, redirectToRegister } = useLogin(false);
  const query = useQueryParams({ preserveParams: true });

  const redeemParam = query.redeem;
  const isRedeeming = redeemParam === '';
  const setupChurchName = query.setupChurch;
  const training = useMemo(() => selectTraining(onboardingName), [onboardingName]);

  const [isLoading, setIsLoading] = useState(true);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const redeem = (selectedTraining: Training) => {
    if (!user) {
      return;
    }

    Promise.all([getTrainingOverview(user.userId, false), getTrainingOverview(user.userId, true)])
      .then(([assigned, completed]) => {
        const trainings = [...(assigned ?? []), ...(completed ?? [])];
        const hasTrainingAlready = trainings.some(
          assignedTraining => assignedTraining.originalTrainingId === selectedTraining.trainingId
        );

        if (hasTrainingAlready) {
          windowService.redirectTo('/dashboard/training');
          return;
        }

        return selfAssignTraining(selectedTraining.trainingId, user.userId, user.lastSelectedAccount || '')
          .then(() => {
            alertService.showOnNextPage('Training Assigned');
            windowService.redirectTo('/dashboard/training');
          })
          .catch((error: Error) => {
            handleError(error);
            setIsLoading(false);
          });
      })
      .catch((error: Error) => {
        handleError(error);
        setIsLoading(false);
      });
  };

  const setupChurch = (churchName: string, selectedTraining: Training) => {
    if (!user) {
      return;
    }

    const newOrgId = uuid.generate();

    createOrgAccount(newOrgId, churchName, user.userId)
      .then(() => {
        windowService.redirectTo(`/assign-training/${selectedTraining.trainingId}`);
      })
      .catch((error: Error) => {
        handleError(error);
        setIsWaiting(false);
        setIsLoading(false);
      });
  };

  const handleEmailSubmission = (
    preEncodedEmail: string,
    userType: string,
    url?: string | null,
    callback?: () => void
  ) => {
    const emailLower = (preEncodedEmail || '').toLowerCase();
    setIsAuthenticating(true);

    if (userType === 'Pastor') {
      getMemberByEmail(emailLower).then((orgs: OrgMembership[]) => {
        const isGridUser = orgs.length > 0;
        const siteWideIds = LIFEWAY_ORG_IDS;
        const filteredOrgs = orgs.filter(membership => {
          const orgId = membership.org.id;
          return !!orgId && !siteWideIds.includes(orgId);
        });

        if (isGridUser && filteredOrgs.length > 0 && training) {
          const redirectPath = url ? url : `/onboarding/churchcares/church-select/${training.trainingId}`;
          userService
            .getUserIdByEmail(emailLower)
            .then((response: UserLookupResponse) => {
              setIsAuthenticating(false);
              if (response?.id) {
                redirectToLogin(redirectPath, emailLower);
              } else {
                redirectToRegister(redirectPath);
              }
            })
            .catch((error: Error) => {
              handleError(error);
              setIsAuthenticating(false);
              callback?.();
            });
          return;
        } else {
          setIsAuthenticating(false);
          callback?.();
        }
      });
    } else {
      const redirectPath = url ? url : `/onboarding/${onboardingName}?redeem`;
      userService
        .getUserIdByEmail(emailLower)
        .then((result: UserLookupResponse) => {
          setIsAuthenticating(false);
          if (result?.id) {
            redirectToLogin(redirectPath, emailLower);
          } else {
            redirectToRegister(redirectPath);
          }
        })
        .catch(() => {
          // No user found, so we need to register
          redirectToRegister(redirectPath);
        });
    }
  };

  const handleChurchSetupRequest = (preEncodedEmail: string, churchName: string) => {
    const emailLower = preEncodedEmail.toLowerCase();
    const redirectPath = `/onboarding/${onboardingName}?setupChurch=${encodeURIComponent(churchName)}`;

    userService
      .getUserIdByEmail(emailLower)
      .then((result: UserLookupResponse) => {
        if (result?.id) {
          redirectToLogin(redirectPath, emailLower);
        } else {
          redirectToRegister(redirectPath);
        }
      })
      .catch(() => {
        redirectToRegister(redirectPath);
      });
  };

  const handleLifeWayRedirection = (preEncodedEmail: string) => {
    const emailLower = (preEncodedEmail || '').toLowerCase();

    userService
      .getUserIdByEmail(emailLower)
      .then((result: UserLookupResponse) => {
        if (result?.id) {
          redirectToLogin(undefined, emailLower);
        } else {
          redirectToRegister();
        }
      })
      .catch(redirectToRegister);
  };

  useEffect(() => {
    if (!training) {
      setIsLoading(false);
      return;
    }

    if (user && isRedeeming) {
      redeem(training);
    } else if (user && setupChurchName) {
      const hasRedirectURL = user?.redirectURL && user.redirectURL.includes('/onboarding/');
      if (hasRedirectURL) {
        setIsLoading(false);
        setIsWaiting(true);
        setupChurch(setupChurchName, training);
      } else {
        windowService.redirectTo('/dashboard');
      }
    } else {
      setIsLoading(false);
      setIsWaiting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRedeeming, setupChurchName, training, user]);

  if (isLoading) {
    return <LoadingState>Logging in</LoadingState>;
  }

  if (isWaiting) {
    return <LoadingState>Setting up</LoadingState>;
  }

  return (
    <OnboardingContainer>
      <Wizard
        handleChurchSetupRequest={handleChurchSetupRequest}
        handleEmailSubmission={handleEmailSubmission}
        handleLifeWayRedirection={handleLifeWayRedirection}
        isAuthenticating={isAuthenticating}
        loggedInEmail={user?.logInEmail ?? ''}
      />
    </OnboardingContainer>
  );
};
